import { createSlice, PayloadAction, createAsyncThunk, isPending, isRejected, isFulfilled, createSelector } from '@reduxjs/toolkit';
import { IUser } from '../models/IUser';
import { IVisaCard } from '../models/IVisaCard';
import { api } from '../api';
import { RootState } from './store';
import { IVisaCountry } from '../models/IVisaCountry';

export interface VisaProgramState {
	countries: IVisaCountry[];
	cards: IVisaCard[];
	currentCardIndex: number;
	loading: boolean;
	error: string | null;
}

const initialState: VisaProgramState = {
	countries: [],
	cards: [],
  currentCardIndex: 0,
  loading: false,
  error: null,
};

// Async thunks
export const awaitWithLoading = createAsyncThunk(
  'visaProgram/awaitWithLoading',
  async (
    payload: { timeout: number } = { timeout: 0 },
    { rejectWithValue }
  ) => {
    try {
      return await new Promise(r => setTimeout(r, payload.timeout));
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const initializeVisaTest = createAsyncThunk(
  'visaProgram/initializeVisaTest',
  async (
    payload: {} = {},
    { rejectWithValue }
  ) => {
    try {
      return await api.getVisaApplicabilityQuestions();
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const answerVisaQuestion = createAsyncThunk(
	'visaProgram/answerVisaQuestion',
	async (
		payload: {
			cardId: number;
			value: string;
			sessionId: string;
		},
		{ rejectWithValue }
	) => {
		try {
			return await api.answerVisaQuestion({ ...payload });
		} catch (err: any) {
			return rejectWithValue(err.message);
		}
	}
);

export const getVisaResult = createAsyncThunk(
	'visaProgram/getVisaResult',
	async (
		payload: {
			sessionId: string;
		},
		{ rejectWithValue }
	) => {
		try {
			return await api.getVisaResultLongPolling({ ...payload });
		} catch (err: any) {
			return rejectWithValue(err.message);
		}
	}
);

const visaProgramSlice = createSlice({
  name: 'visaProgram',
  initialState,
  reducers: {
    activateNextVisaCard(state) {
      state.currentCardIndex += 1;
    },
    resetVisaQuestions(state) {
			state.countries = [];
      state.currentCardIndex = 0;
			state.error = null;
			state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
		// Initial Card Fetching
			.addCase(initializeVisaTest.fulfilled, (state, action) => {
				state.cards = (action.payload || []).sort((a, b) => a.id - b.id)
			})
			.addCase(getVisaResult.fulfilled, (state, action) => {
				console.log(action.payload);
				state.countries = action.payload;
			})
			.addMatcher(
				isPending,
				(state) => {
					state.loading = true;
					state.error = null;
				}
			)
			.addMatcher(
				isRejected,
				(state, action) => {
					state.loading = false;
					state.error = action.payload as string || action.error.message || 'Unknown error';
				}
			)
			.addMatcher(
				isFulfilled,
				(state) => {
					state.loading = false;
					state.error = null;
				}
			);
  },
});

export default visaProgramSlice;
export const {
  activateNextVisaCard,
  resetVisaQuestions,
} = visaProgramSlice.actions;

// selectors
export const selectVisaSectionNumber = createSelector(
  [(state: RootState) => state.visaProgram.currentCardIndex],
  (currentCardIndex) => Math.floor(currentCardIndex / 10)
);

export const selectVisaCountries = createSelector(
  [(state: RootState) => state.visaProgram.countries],
  (countries) => 
    [...countries]
      .sort((a, b) => b.total_score - a.total_score)
);

export const selectVisaTotalSectionsCount = createSelector(
  [],
  () => 1
);

